import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CLAIMS_ACTIONS } from '../actions/claims.actions';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { ClaimsService } from '../../services';
import { of } from 'rxjs';
import { ClaimModel } from '../../../shared/models';
import {
  EClaimsGroup,
  EXAGO_CLAIMS,
  getApplicationClaims,
  UNNORMALIZED_ADMIN_ACCOUNT_CLAIMS,
  UNNORMALIZED_CONVERSATIONS_ACE_INSIGHTS_CLAIMS,
} from '../../configurations/claims.config';
import { APPLICATION_ACTIONS } from '../actions/application.actions';
import { AUTH_SELECTORS } from '../selectors/auth.selectors';
import { Store } from '@ngrx/store';

@Injectable()
export class ClaimsEffects {
  constructor(
    private readonly actions: Actions,
    private readonly claimService: ClaimsService,
    private readonly store: Store,
  ) {}

  getClaims$ = createEffect(() =>
    this.actions.pipe(
      ofType(CLAIMS_ACTIONS.getClaims),
      switchMap(() => this.claimService.getClaims()),
      withLatestFrom(this.store.select(AUTH_SELECTORS.hasExagoRole)),
      map(([claims, hasExagoRole]) => {
        const normalizedClaims = this.getNormalizedClaims(claims);
        const filteredApplicationClaims = this.getFilteredApplicationClaims(normalizedClaims);
        const updatedClaimsWithExago = this.getUpdatedClaimsWithExago(filteredApplicationClaims, hasExagoRole);

        return CLAIMS_ACTIONS.getClaimsSuccess(updatedClaimsWithExago);
      }),
      catchError(() => of(CLAIMS_ACTIONS.getClaimsError())),
    ),
  );

  getClaimsError$ = createEffect(() =>
    this.actions.pipe(
      ofType(CLAIMS_ACTIONS.getClaimsError),
      map(() => APPLICATION_ACTIONS.hideLoader()),
    ),
  );

  private getFilteredApplicationClaims(claims: ClaimModel[]): string[] {
    const result: string[] = [];
    const applicationClaims = Object.values(getApplicationClaims());
    for (let i = 0; i < claims.length; i++) {
      if (applicationClaims.includes(claims[i].Value)) {
        result.push(claims[i].Value);
      }
    }
    return result;
  }

  private getUpdatedClaimsWithExago(filteredClaims: string[], hasExagoRole: boolean): string[] {
    if (hasExagoRole) {
      const exagoClaimIsInList: boolean = filteredClaims.some((claim) => claim === EXAGO_CLAIMS.exagoAllAccessRead);
      return exagoClaimIsInList ? filteredClaims : [...filteredClaims, EXAGO_CLAIMS.exagoAllAccessRead];
    }
    return filteredClaims.filter((claim) => claim !== EXAGO_CLAIMS.exagoAllAccessRead);
  }

  private getNormalizedClaims(claims: ClaimModel[]): ClaimModel[] {
    return claims.map((claim) => {
      if (Object.values(UNNORMALIZED_ADMIN_ACCOUNT_CLAIMS).includes(claim.Value)) {
        claim.Value = `${EClaimsGroup.account}${claim.Value}`;
      }
      if (Object.values(UNNORMALIZED_CONVERSATIONS_ACE_INSIGHTS_CLAIMS).includes(claim.Value)) {
        claim.Value = `${EClaimsGroup.aceInsights}${claim.Value}`;
      }
      return claim;
    });
  }
}
